import React, { FC } from 'react';
import { View } from 'react-native';

import { font as initialFont } from '@styles/fonts';
import { fullHeight, fullWidth, scale as initialScale } from '@components/frame';
import ColorWrapper, { WithColor } from '@styles/colors';
import Loading from '@components/loading';
import { ImageContext, ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types.d';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import SlidersWrapper from '@v2/components/imageHandling/components/SlidersWrapper';
import { useKeyboardNavigation } from './hooks/useKeyboardNavigation';
import { useManagePicture } from './hooks/useManagePicture';
import NavigationArrows from '@v2/components/imageHandling/components/NavigationArrows';
import { PagesNavigation } from '@v2/components/imageHandling/components/PagesNavigation';
import { PictureProvider } from '@v2/components/imageHandling/providers/PictureProvider';
import NoData from '@v2/components/imageHandling/components/NoData';
import { ContainerStyle } from '@interfaces/base';

export interface PictureProps extends ContainerStyle, WithColor {
  onImagePress?: () => void;
  imageContext: ImageContext;
  images: ImageDocumentStoreItem[];
  loaded: boolean;
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  withoutBottom?: boolean;
  triggerShowActions?: () => void;
  showActions?: boolean;
  isSingleImage?: boolean;
  withoutBackButtonForNoData?: boolean;
  shouldUseColorWrapperForBigSliderBg?: boolean;
  shouldUseColorWrapperForSmallSliderBg?: boolean;
  width?: number;
  height?: number;
  smallSliderBg?: string;
  bg?: string;
  scale?: ScaleFunction;
  font?: typeof initialFont;
  singleImageInBigSlider?: boolean;
  withoutAttachItems?: boolean;
  withNotchTop?: boolean;
  /** Optional overlay rendered inside the native Zoomable's content (e.g. annotation surface). */
  zoomableOverlay?: React.ReactNode;
}

const Picture: FC<PictureProps> = (props) => {
  const {
    imageContext,
    images,
    C,
    loaded = true,
    withoutBackButtonForNoData = false,
    width = fullWidth,
    height = fullHeight,
    selectedIndex,
    setSelectedIndex,
    scale = initialScale,
    font = initialFont,
    shouldUseColorWrapperForBigSliderBg,
    bg,
    isSingleImage,
    triggerShowActions,
    containerStyle,
    showActions = true,
  } = props;

  useKeyboardNavigation({ images, selectedIndex, setSelectedIndex });

  const { onImageZoomStart } = useManagePicture({ showActions, triggerShowActions });

  const shouldShowSlider = loaded && images.length > 0;
  const shouldShowNoData = loaded && images.length === 0;

  return (
    <View style={[{ width: width ?? fullWidth, height: height ?? fullHeight, flex: 1 }, containerStyle]}>
      {shouldShowNoData && <NoData {...{ C, withoutBackButtonForNoData, bg, shouldUseColorWrapper: shouldUseColorWrapperForBigSliderBg }} />}

      {!shouldShowSlider && !shouldShowNoData && <Loading {...(bg ? { background: !shouldUseColorWrapperForBigSliderBg ? bg : C(bg) } : {})} />}

      <PictureProvider imageContext={imageContext} onImageZoomStart={onImageZoomStart}>
        {/* `props` already contains zoomableOverlay; SlidersWrapper extends PictureProps so it spreads through to BigSlider → ProgressiveImageTabletMobile. */}
        {shouldShowSlider && <SlidersWrapper {...props} />}
        <PagesNavigation {...{ C, image: images[selectedIndex], showActions, width, height, scale, font }} />
        {shouldShowSlider && !isSingleImage && <NavigationArrows {...props} images={images} scale={scale} />}
      </PictureProvider>
    </View>
  );
};

export default ColorWrapper(Picture);
